import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Home, Layers, CalendarClock, MessagesSquare, Wallet, Star, LineChart,
  Settings, Users, ClipboardList, Package, Repeat, Send, Bell,
  Heart, ShieldAlert, MapPin, Building2, CreditCard, Ticket,
  Image as ImageIcon, ShieldCheck, LayoutDashboard, Menu as MenuIcon, X,
  Receipt, Globe, Palmtree, CalendarCheck, Tag, BellRing, Smartphone,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const CUSTOMER_NAV = [
  { to: "/dashboard", label: "Overview", icon: Home, end: true },
  { to: "/dashboard/bookings", label: "My bookings", icon: CalendarClock },
  { to: "/dashboard/orders", label: "My orders", icon: Package },
  { to: "/dashboard/requests", label: "My requests", icon: ClipboardList },
  { to: "/dashboard/turnovers", label: "Turnovers", icon: CalendarCheck },
  { to: "/dashboard/recurring", label: "Recurring", icon: Repeat },
  { to: "/dashboard/receipts", label: "Receipts", icon: Wallet },
  { to: "/dashboard/wallet", label: "Wallet", icon: CreditCard, badgeKey: "credit_balance" },
  { to: "/dashboard/addresses", label: "Addresses", icon: MapPin },
  { to: "/dashboard/business-profile", label: "Business profile", icon: Building2 },
  { to: "/dashboard/messages", label: "Messages", icon: MessagesSquare, badgeKey: "unread_messages" },
  { to: "/dashboard/notifications", label: "Notifications", icon: Bell },
  { to: "/dashboard/deal-alerts", label: "My deal alerts", icon: BellRing },
  { to: "/dashboard/reviews", label: "Reviews", icon: Star },
  { to: "/dashboard/disputes", label: "Disputes", icon: ShieldAlert },
  { to: "/favorites", label: "Favorites", icon: Heart },
  { to: "/dashboard/support", label: "Support", icon: Ticket },
];

const PROVIDER_NAV = [
  { to: "/dashboard/jobs", label: "Active jobs", icon: Smartphone },
  { to: "/dashboard/services", label: "My services", icon: Layers },
  { to: "/dashboard/availability", label: "Availability", icon: CalendarClock },
  { to: "/dashboard/calendar", label: "Calendar", icon: CalendarClock },
  { to: "/dashboard/find-work", label: "Job matches", icon: Send },
  { to: "/dashboard/earnings", label: "Earnings", icon: Wallet, capability: "view_financials" },
  { to: "/dashboard/featured", label: "Featured", icon: Star },
  { to: "/dashboard/portfolio", label: "Portfolio", icon: ImageIcon },
  { to: "/dashboard/external-reviews", label: "Web reviews", icon: Globe },
  { to: "/dashboard/deals", label: "Deals", icon: Tag },
  { to: "/dashboard/vacation-specialties", label: "Vacation rentals", icon: Palmtree },
  { to: "/dashboard/expenses", label: "Expenses", icon: Receipt, capability: "view_financials" },
  { to: "/dashboard/verification", label: "Verification", icon: ShieldCheck },
  { to: "/dashboard/plans", label: "Plans", icon: CreditCard },
  { to: "/dashboard/team", label: "Team", icon: Users, capability: "manage_team" },
  { to: "/dashboard/analytics", label: "Analytics", icon: LineChart, capability: "view_reports" },
];

const ADMIN_NAV = [
  { to: "/admin", label: "Admin console", icon: LayoutDashboard, roles: ["administrator", "super_administrator", "moderator", "support_agent"] },
];

function NavBody({ isProvider, counts, onNavigate }) {
  return (
    <>
      <div className="px-3 pb-2 pt-2">
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Customer</div>
      </div>
      <nav className="space-y-1">
        {CUSTOMER_NAV.map((n) => <NavItem key={n.to} {...n} counts={counts} onNavigate={onNavigate} />)}
      </nav>
      {isProvider && (
        <>
          <div className="px-3 pb-2 pt-4">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Provider</div>
          </div>
          <nav className="space-y-1">
            {PROVIDER_NAV.map((n) => <NavItem key={n.to} {...n} counts={counts} onNavigate={onNavigate} />)}
          </nav>
        </>
      )}
      <div className="mt-4 pt-3 border-t border-slate-100">
        <NavLink
          to="/account/settings"
          onClick={onNavigate}
          className="flex items-center gap-3 px-3 py-2 text-sm rounded-xl text-[#252A34]/80 hover:bg-slate-50"
          data-testid="dashboard-account-settings"
        >
          <Settings className="h-4 w-4" /> Account settings
        </NavLink>
      </div>
    </>
  );
}

export default function DashboardSidebar() {
  const { user } = useAuth();
  const [counts, setCounts] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    api.get("/dashboard/transactions/summary").then((r) => setCounts(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  const isProvider = user?.roles?.some((r) => r === "provider" || r === "team_member") || counts?.provider;

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-64 shrink-0 pr-6">
        <div className="sticky top-24 rounded-2xl border border-slate-200 bg-white p-3" data-testid="dashboard-sidebar">
          <NavBody isProvider={isProvider} counts={counts} />
        </div>
      </aside>

      {/* Mobile trigger row (rendered in-flow above main content) */}
      <div className="lg:hidden w-full mb-4">
        <button
          onClick={() => setMobileOpen(true)}
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-[#071b22] shadow-sm"
          data-testid="dashboard-mobile-menu-btn"
          aria-label="Open dashboard menu"
        >
          <MenuIcon className="h-4 w-4" /> Dashboard menu
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 z-40 bg-slate-900/50"
            onClick={() => setMobileOpen(false)}
            data-testid="dashboard-mobile-overlay"
          />
          <aside
            className="lg:hidden fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-white shadow-2xl flex flex-col overflow-hidden"
            data-testid="dashboard-mobile-drawer"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 shrink-0">
              <div className="font-display font-semibold text-[#071b22]">Your dashboard</div>
              <button
                onClick={() => setMobileOpen(false)}
                className="rounded-lg p-1.5 hover:bg-slate-100"
                data-testid="dashboard-mobile-close"
                aria-label="Close dashboard menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              <NavBody isProvider={isProvider} counts={counts} onNavigate={() => setMobileOpen(false)} />
            </div>
          </aside>
        </>
      )}
    </>
  );
}

function NavItem({ to, label, icon: Icon, end, badgeKey, counts, onNavigate }) {
  const testid = `dashboard-nav-${label.toLowerCase().replace(/\s+/g, "-")}`;
  const badge = badgeKey ? counts?.[badgeKey] : null;
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 text-sm rounded-xl transition-colors ${
          isActive ? "bg-[#165a71]/10 text-[#071b22] font-medium" : "text-[#252A34]/80 hover:bg-slate-50"
        }`
      }
    >
      <Icon className="h-4 w-4" />
      <span className="flex-1">{label}</span>
      {badge > 0 && <span className="rounded-full bg-[#165a71] text-white text-[10px] px-2 py-0.5 font-semibold">{badge}</span>}
    </NavLink>
  );
}
